import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PERMISSIONS, type Permission } from '@wms/shared';
import { apiFetch, ApiError } from '../../lib/api';
import { useElevatedFetch } from '../../lib/elevation';
import { useCan, useSession } from '../../lib/session';
import { useCurrentWarehouseId } from '../../lib/warehouse';
import { diffAgainstRole, PermissionPicker, presetSelection, type PresetKey } from './permission-picker';

interface UserListItem {
  readonly id: string;
  /** 【U-06】登录标识，公司内唯一 */
  readonly username: string;
  /** 【U-06】可选联系邮箱 */
  readonly email: string | null;
  readonly displayName: string;
  readonly status: 'ACTIVE' | 'SUSPENDED' | 'DELETED';
  readonly lastLoginAt: string | null;
  readonly hasOverridePin: boolean;
}

export function UsersPage(): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const elevatedFetch = useElevatedFetch();
  const { data: session } = useSession();
  // user.restore = 顶尖标志：能看"已删除账号"区、能解锁顶尖层权限
  const isTop = useCan(PERMISSIONS.USER_RESTORE);

  const { data, isPending } = useQuery({
    queryKey: ['users', isTop],
    queryFn: () =>
      apiFetch<{ items: UserListItem[] }>(`/users${isTop ? '?includeDeleted=true' : ''}`),
  });
  const activeUsers = useMemo(
    () => (data?.items ?? []).filter((u) => u.status !== 'DELETED'),
    [data],
  );
  const deletedUsers = useMemo(
    () => (data?.items ?? []).filter((u) => u.status === 'DELETED'),
    [data],
  );

  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserListItem | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['users'] }).then(() => undefined);

  const doDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setRowError(null);
    try {
      // DELETE 统一带空对象 body——提权指纹在签发端与守卫端都对 {} 计算（与
      // user-detail 页 revokeRole/removeOverride 同一既有约定）
      await elevatedFetch(PERMISSIONS.USER_MANAGE, `/users/${deleteTarget.id}`, {}, 'DELETE');
      setDeleteTarget(null);
      await refresh();
    } catch (err) {
      setDeleteTarget(null);
      if (!(err instanceof Error && err.message === 'elevation_cancelled')) {
        setRowError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
      }
    }
  };

  const doRestore = async (userId: string): Promise<void> => {
    setRowError(null);
    try {
      await elevatedFetch(PERMISSIONS.USER_RESTORE, `/users/${userId}/restore`, {});
      await refresh();
    } catch (err) {
      if (!(err instanceof Error && err.message === 'elevation_cancelled')) {
        setRowError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
      }
    }
  };

  return (
    <div>
      <div className="row row--between">
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('users.title')}</h1>
        <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
          {t('users.create')}
        </button>
      </div>

      {rowError && <p className="field-error">{rowError}</p>}
      {isPending && <p>{t('app.loading')}</p>}
      {data && (
        <div className="table-scroll"><table className="table">
          <thead>
            <tr>
              <th>{t('users.colName')}</th>
              <th>{t('users.colUsername')}</th>
              <th>{t('users.colEmail')}</th>
              <th>{t('users.colStatus')}</th>
              <th>{t('users.colLastActive')}</th>
              <th>{t('users.colPin')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {activeUsers.map((u) => (
              <tr key={u.id}>
                <td>
                  {/* 【用户反馈】下划线链接太丑——按钮样式，语义仍是导航链接 */}
                  <Link className="btn btn--ghost btn--sm" to={`/settings/users/${u.id}`}>
                    {u.displayName}
                  </Link>
                </td>
                <td>{u.username}</td>
                <td>{u.email ?? '—'}</td>
                <td>
                  <span
                    className={`badge ${u.status === 'ACTIVE' ? 'badge--success' : 'badge--muted'}`}
                  >
                    {t(u.status === 'ACTIVE' ? 'users.statusActive' : 'users.statusSuspended')}
                  </span>
                </td>
                <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</td>
                <td>{u.hasOverridePin ? '✓' : ''}</td>
                <td>
                  {/* 软删：自己删不了自己；防提权（碰不到更强账号）由服务层强制 */}
                  {u.id !== session?.userId && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      style={{ color: 'var(--color-danger)' }}
                      onClick={() => setDeleteTarget(u)}
                    >
                      {t('app.delete')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {/* 【用户裁决】已删除账号只对顶尖可见：提示条 + 一键恢复；永不物理删除 */}
      {isTop && deletedUsers.length > 0 && (
        <section className="section" style={{ marginTop: 'var(--space-5)' }}>
          <h2>{t('users.deletedSection')}</h2>
          <p className="field-hint">{t('users.deletedNotice', { count: deletedUsers.length })}</p>
          {deletedUsers.map((u) => (
            <div
              key={u.id}
              className="row row--between"
              style={{ padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-border)' }}
            >
              <span style={{ opacity: 0.7 }}>
                {u.displayName} · {u.username}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => void doRestore(u.id)}
              >
                {t('users.restore')}
              </button>
            </div>
          ))}
        </section>
      )}

      {showCreate && <CreateUserModal isTop={isTop} onClose={() => setShowCreate(false)} />}

      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('users.confirmDeleteTitle')}</h2>
            <p>{t('users.confirmDeleteBody', { name: deleteTarget.displayName })}</p>
            <p className="field-hint">{t('users.confirmDeleteHint')}</p>
            <div className="modal__actions">
              <button className="btn btn--ghost" onClick={() => setDeleteTarget(null)}>
                {t('app.cancel')}
              </button>
              <button
                className="btn btn--danger"
                onClick={() => void doDelete()}
              >
                {t('app.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 【用户设计 2026-08-17】创建账号 = 基本信息 + 分层权限选择。
 * 提交为单次请求（role 预设 + 相对预设的 grants/denies 差量），只弹一次
 * 提权密码；顶尖层的解锁按钮走同一个 /elevation/grant 密码重验。
 */
function CreateUserModal({
  isTop,
  onClose,
}: {
  readonly isTop: boolean;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const elevatedFetch = useElevatedFetch();

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [preset, setPreset] = useState<PresetKey>('EMPLOYEE');
  const [selected, setSelected] = useState<Set<Permission>>(() => presetSelection('EMPLOYEE'));
  // 【用户实测】没有仓库访问权的新账号进不了任何页面——默认勾选当前仓库
  const currentWarehouseId = useCurrentWarehouseId();
  const { data: warehousesData } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => apiFetch<{ items: { id: string; name: string }[] }>('/warehouses'),
  });
  const [warehouseIds, setWarehouseIds] = useState<Set<string>>(
    () => new Set(currentWarehouseId ? [currentWarehouseId] : []),
  );
  const toggleWarehouse = (id: string): void => {
    setWarehouseIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const [topUnlocked, setTopUnlocked] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  const applyPreset = (next: PresetKey): void => {
    setPreset(next);
    setSelected(presetSelection(next));
  };
  const toggle = (p: Permission): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const unlockTop = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setUnlocking(true);
    setUnlockError(null);
    try {
      // 密码重验：借道 /elevation/grant（同一套指纹绑定提权），令牌不使用——
      // 解锁只是恢复前端可点性，真正的授出上限由服务层防提权闸门强制
      await apiFetch('/elevation/grant', {
        method: 'POST',
        body: {
          scope: PERMISSIONS.USER_MANAGE,
          method: 'password',
          password: unlockPassword,
          intendedMethod: 'POST',
          intendedPath: '/users',
          intendedRequestBody: null,
        },
      });
      setTopUnlocked(true);
      setShowUnlock(false);
      setUnlockPassword('');
    } catch (err) {
      setUnlockError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    } finally {
      setUnlocking(false);
    }
  };

  const create = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { grants, denies } = diffAgainstRole(selected, preset);
      await elevatedFetch(PERMISSIONS.USER_MANAGE, '/users', {
        username,
        // 空邮箱不提交该字段，让后端落 NULL（部分唯一索引只跳过 NULL）
        ...(email.trim() ? { email: email.trim() } : {}),
        displayName,
        temporaryPassword,
        mustChangePassword: true,
        role: preset,
        ...(grants.length ? { grants } : {}),
        ...(denies.length ? { denies } : {}),
        ...(warehouseIds.size ? { warehouseIds: [...warehouseIds] } : {}),
      });
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (err) {
      if (!(err instanceof Error && err.message === 'elevation_cancelled')) {
        setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form
        className="modal"
        style={{ maxWidth: '720px', width: '92vw' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void create(e)}
      >
        <h2>{t('users.createTitle')}</h2>
        <div className="row" style={{ gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <label className="field" style={{ flex: '1 1 200px' }}>
            <span>{t('users.displayName')}</span>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          </label>
          <label className="field" style={{ flex: '1 1 200px' }}>
            <span>{t('users.username')}</span>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              /* 【U-06】与后端 zod 同一条规则：不允许空格与 @ */
              pattern="[^\s@]+"
              maxLength={64}
              required
            />
          </label>
        </div>
        <div className="row" style={{ gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <label className="field" style={{ flex: '1 1 200px' }}>
            <span>{t('users.emailOptional')}</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="field" style={{ flex: '1 1 200px' }}>
            <span>{t('users.temporaryPassword')}</span>
            <input
              type="text"
              minLength={12}
              value={temporaryPassword}
              onChange={(e) => setTemporaryPassword(e.target.value)}
              required
            />
          </label>
        </div>

        <div className="field">
          <span>{t('users.warehouseAccess')}</span>
          <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--space-1)' }}>
            {(warehousesData?.items ?? []).map((w) => (
              <button
                key={w.id}
                type="button"
                className={warehouseIds.has(w.id) ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
                aria-pressed={warehouseIds.has(w.id)}
                onClick={() => toggleWarehouse(w.id)}
              >
                {w.name}
              </button>
            ))}
          </div>
          <span className="field-hint">{t('users.warehouseAccessHint')}</span>
        </div>

        <h3 style={{ margin: 'var(--space-2) 0' }}>{t('users.permissionsTitle')}</h3>
        <PermissionPicker
          selected={selected}
          onToggle={toggle}
          topUnlocked={topUnlocked}
          canUnlockTop={isTop}
          onRequestUnlock={() => setShowUnlock(true)}
          onApplyPreset={applyPreset}
          activePreset={preset}
        />

        {error && <p className="field-error">{error}</p>}
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('app.cancel')}
          </button>
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {t('users.create')}
          </button>
        </div>

      </form>

      {/* 解锁弹窗必须在创建表单之外——嵌套 form 是无效 HTML */}
      {showUnlock && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setShowUnlock(false);
          }}
        >
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => void unlockTop(e)}
          >
            <h2>{t('users.unlockTitle')}</h2>
            <p className="field-hint">{t('users.unlockHint')}</p>
            <label className="field">
              <span>{t('elevation.passwordLabel')}</span>
              <input
                type="password"
                autoFocus
                value={unlockPassword}
                onChange={(e) => setUnlockPassword(e.target.value)}
                required
              />
            </label>
            {unlockError && <p className="field-error">{unlockError}</p>}
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setShowUnlock(false)}>
                {t('app.cancel')}
              </button>
              <button type="submit" className="btn btn--primary" disabled={unlocking || !unlockPassword}>
                {t('users.unlockConfirm')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
